import { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Grid, Card, CardContent, CircularProgress,
  Button, ButtonGroup, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
  Paper, Chip, Snackbar, Alert
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FilterAlt as FilterIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, subDays, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import reportsService from '../services/reportsService';

// Paleta de cores para gráficos de pizza
const CHART_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2',
  '#0097a7', '#5d4037', '#455a64', '#c2185b', '#1565c0'
];

// Cores específicas por status
const STATUS_COLORS = {
  'Confirmado': '#1976d2',
  'Realizado': '#388e3c',
  'Cancelado': '#d32f2f',
  'Pendente': '#f57c00',
};

// Formatar moeda
const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

// Formatar data para input HTML
const toInputDate = (date) => format(date, 'yyyy-MM-dd');

// Formatar data para exibição
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
};

// Status chip com cor
const StatusChip = ({ status }) => {
  const colorMap = {
    'Confirmado': 'primary',
    'Realizado': 'success',
    'Cancelado': 'error',
    'Pendente': 'warning'
  };
  return <Chip label={status} color={colorMap[status] || 'default'} size="small" />;
};

// Label customizado para pizza
const renderCustomLabel = ({ name, value, percent }) => {
  if (percent < 0.05) return null; // Não mostrar labels < 5%
  return `${name}: ${Math.round(percent * 100)}%`;
};

// Componente de gráfico de pizza reutilizável
const PieChartCard = ({ title, data, isCurrency = false }) => {
  const hasData = data && data.length > 0;
  const colors = title.includes('Status')
    ? data.map(d => STATUS_COLORS[d.name] || '#999')
    : CHART_COLORS;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        {hasData ? (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                nameKey="name"
                label={renderCustomLabel}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={entry.name} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => isCurrency ? formatCurrency(value) : value}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary" variant="body2">Sem dados no período</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Componente de tabela de top clientes
const TopClientsCard = ({ title, data }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      {data?.byFrequency?.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Por frequência
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>Qtd</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.byFrequency.map((client, i) => (
                  <TableRow key={`freq-${i}`}>
                    <TableCell sx={{ py: 0.5 }}>{client.clientName}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>{client.count}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>{formatCurrency(client.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Por valor
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>Qtd</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.byValue.map((client, i) => (
                  <TableRow key={`val-${i}`}>
                    <TableCell sx={{ py: 0.5 }}>{client.clientName}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>{client.count}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>{formatCurrency(client.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary" variant="body2">Sem dados</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

export default function ReportsPage() {
  // Estado do filtro de datas
  // endDate padrão: +30 dias no futuro para incluir agendamentos futuros já cadastrados
  const [startDate, setStartDate] = useState(toInputDate(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(toInputDate(addDays(new Date(), 30)));

  // Estado dos dados
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado da tabela paginada
  const [appointments, setAppointments] = useState({ items: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 0 });
  const [tableLoading, setTableLoading] = useState(false);
  const [sortBy, setSortBy] = useState('startTime');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0); // MUI usa 0-indexed
  const [pageSize, setPageSize] = useState(20);

  // Carregar dados do summary
  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportsService.getSummary(startDate, endDate);
      setSummary(data);
    } catch (err) {
      console.error('[Reports] Erro ao carregar summary:', err);
      setError(err.response?.data?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Carregar tabela paginada
  const loadAppointments = useCallback(async () => {
    setTableLoading(true);
    try {
      const data = await reportsService.getAppointments({
        startDate,
        endDate,
        page: page + 1, // API usa 1-indexed
        pageSize,
        sortBy,
        sortDirection
      });
      setAppointments(data);
    } catch (err) {
      console.error('[Reports] Erro ao carregar agendamentos:', err);
    } finally {
      setTableLoading(false);
    }
  }, [startDate, endDate, page, pageSize, sortBy, sortDirection]);

  // Carregar dados iniciais
  useEffect(() => {
    loadSummary();
    loadAppointments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recarregar tabela quando paginação/ordenação muda
  useEffect(() => {
    if (!loading) {
      loadAppointments();
    }
  }, [page, pageSize, sortBy, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplicar filtro
  const handleFilter = () => {
    // Validar período máximo de 90 dias
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (diffDays > 120) {
      setError('Período máximo permitido: 120 dias');
      return;
    }
    if (start >= end) {
      setError('Data inicial deve ser anterior à data final');
      return;
    }
    setPage(0);
    loadSummary();
    loadAppointments();
  };

  // Presets de período: exibe os últimos X dias + próximos 30 dias (agendamentos futuros)
  const setPreset = (days) => {
    setStartDate(toInputDate(subDays(new Date(), days)));
    setEndDate(toInputDate(addDays(new Date(), 30)));
  };

  // Ordenação da tabela
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(0);
  };

  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Relatórios
      </Typography>

      {/* Filtro de Período */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <AssessmentIcon color="primary" />
          <TextField
            type="date"
            label="Data Inicial"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            type="date"
            label="Data Final"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => setPreset(30)}>30 dias</Button>
            <Button onClick={() => setPreset(60)}>60 dias</Button>
            <Button onClick={() => setPreset(90)}>90 dias</Button>
          </ButtonGroup>
          <Button
            variant="contained"
            startIcon={<FilterIcon />}
            onClick={handleFilter}
            disabled={loading}
          >
            Filtrar
          </Button>
          {loading && <CircularProgress size={24} />}
        </CardContent>
      </Card>

      {summary && (
        <>
          {/* Linha 1: Gráficos de Pizza */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Agendamentos por Status" data={summary.appointmentsByStatus} />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Público vs Privado" data={summary.publicVsPrivate} />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Por Profissional" data={summary.appointmentsByProfessional} />
            </Grid>
          </Grid>

          {/* Linha 2: Gráficos de Pizza */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Por Serviço" data={summary.appointmentsByService} />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Faturamento por Profissional" data={summary.revenueByProfessional} isCurrency />
            </Grid>
            <Grid item xs={12} md={4}>
              <PieChartCard title="Faturamento por Serviço" data={summary.revenueByService} isCurrency />
            </Grid>
          </Grid>

          {/* Linha 3: Tabelas Resumo */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* 3x1: Tabela Resumo */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Resumo do Período
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Agendamentos realizados</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>{summary.summary.totalAppointments}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Confirmados</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: 'primary.main' }}>{summary.summary.confirmedAppointments}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Cancelados</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: 'error.main' }}>{summary.summary.cancelledAppointments}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Clientes registrados</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>{summary.summary.registeredClients}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Faturamento total</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: 'success.main' }}>{formatCurrency(summary.summary.totalRevenue)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Faturamento perdido</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(summary.summary.lostRevenue)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}>Retornos</TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', color: 'info.main' }}>{summary.summary.returnClients}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* 3x2: Top Clientes Período */}
            <Grid item xs={12} md={4}>
              <TopClientsCard title="Top 5 Clientes (Período)" data={summary.topClientsPeriod} />
            </Grid>

            {/* 3x3: Top Clientes All-Time */}
            <Grid item xs={12} md={4}>
              <TopClientsCard title="Top 5 Clientes (Geral)" data={summary.topClientsAllTime} />
            </Grid>
          </Grid>
        </>
      )}

      {/* Tabela Paginada de Agendamentos */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Agendamentos do Período
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: 'startTime', label: 'Data' },
                    { id: 'clientName', label: 'Cliente' },
                    { id: 'serviceName', label: 'Serviço' },
                    { id: 'professionalName', label: 'Profissional' },
                    { id: 'placeName', label: 'Local' },
                    { id: 'status', label: 'Status' },
                    { id: 'price', label: 'Valor' },
                  ].map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : appointments.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum agendamento no período</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  appointments.items.map((apt, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{formatDate(apt.startTime)}</TableCell>
                      <TableCell>{apt.clientName}</TableCell>
                      <TableCell>{apt.serviceName}</TableCell>
                      <TableCell>{apt.professionalName}</TableCell>
                      <TableCell>{apt.placeName}</TableCell>
                      <TableCell><StatusChip status={apt.status} /></TableCell>
                      <TableCell>{formatCurrency(apt.price)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={appointments.totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </CardContent>
      </Card>

      {/* Snackbar de erro */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
